import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AlterLocalizationProvider } from 'bernie-alter';
import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { Layer, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { UIPageStore } from 'stores/ui-page-store';

import { classNames } from 'uitk-react-utils';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';

import { BarRatings, MAX_RATING } from 'components/infosite/property-details/bar-ratings';
import { ReviewsDialog } from 'components/infosite/property-details/reviews-dialog';

const LinkTracked = ClickTracker(Link);

export interface ReviewsProps {
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  reviews?: any;
  isRefreshedHISLayout?: boolean;
  uiPage?: UIPageStore;
}

export type LocalizedReviewsProps = ReviewsProps & LocalizedComponentProps;

export interface ReviewsState {
  initialized: boolean;
}

@withLocalization
@inject('routeStore', 'analytics', 'isRefreshedHISLayout', 'uiPage')
@observer
export class Reviews extends React.Component<LocalizedReviewsProps, ReviewsState> {
  private reviewsAction;
  private reviewsWrapper;

  public constructor(props) {
    super(props);

    this.state = {
      initialized: false
    };

    this.onReviewsDialogClosed = this.onReviewsDialogClosed.bind(this);
  }

  public componentDidMount() {
    this.setState({ initialized: true });
  }

  public render() {
    const { reviews, routeStore, isRefreshedHISLayout, l10n } = this.props;
    const { initialized } = this.state;
    const ratingClassNames = classNames({
      'uitk-type-300 uitk-cell all-t-padding-two all-cell-fill': !isRefreshedHISLayout,
      'uitk-type-500': isRefreshedHISLayout
    });
    const ratingSummaryClassNames = classNames('reviews-summary__rating', {
      'uitk-type-bold uitk-type-300': !isRefreshedHISLayout,
      'uitk-type-bold uitk-type-500': isRefreshedHISLayout
    });
    const countClassNames = classNames('uitk-cell', {
      'uitk-type-right all-cell-shrink': !isRefreshedHISLayout
    });

    const ra = reviewsAction => {
      this.reviewsAction = reviewsAction;
    };
    const rw = reviewsWrapper => {
      this.reviewsWrapper = reviewsWrapper;
    };
    const schema = 'https://schema.org/AggregateRating';

    return (
      <div className="uitk-grid all-cell-1-2" ref={rw} itemProp="aggregateRating" itemScope={true} itemType={schema}>
        <span className={ratingClassNames} data-stid="content-hotel-reviewsummary">
          {reviews &&
            reviews.localizedOverallRating && (
              <React.Fragment>
                <meta itemProp="ratingValue" content={reviews.localizedOverallRating} />
                <span className={ratingSummaryClassNames}>
                  <span className="reviews-summary__rating-value">{`${reviews.localizedOverallRating} / ${MAX_RATING}`}</span>
                </span>
              </React.Fragment>
            )}
          {reviews &&
            reviews.superlative && (
              <React.Fragment>
                <meta itemProp="description" content={reviews.superlative} />
                <span className="reviews-summary__superlative pwa-theme--grey-700 all-x-padding-one">{reviews.superlative}</span>
              </React.Fragment>
            )}
        </span>
        {reviews &&
          reviews.localizedCountWithLabel && (
            <>
              {isRefreshedHISLayout && (
                <BarRatings
                  className="uitk-grid all-cell-1-2 all-y-padding-four all-x-gutter-six all-y-gutter-six"
                  barClassName="uitk-cell"
                  reviews={reviews}
                  l10n={l10n}
                />
              )}
              {initialized && (
                <div className={countClassNames}>
                  <UitkLink>
                    <LinkTracked
                      linkName="Display detailed reviews"
                      disabled={!initialized || null}
                      rfrr="UGC.ViewAllResults"
                      events="event419=10"
                      innerRef={ra}
                      to={{
                        pathname: routeStore.pathname,
                        search: this.props.routeStore.mergeQueryString(routeStore.search, { pwaDialog: 'reviews' })
                      }}
                    >
                      <meta itemProp="reviewCount" content={reviews.count} />
                      {reviews.localizedCountWithLabel}
                      <UitkIcon name="chevron_right" />
                    </LinkTracked>
                  </UitkLink>
                </div>
              )}
              {!initialized && (
                <div className={countClassNames}>
                  <span className="uitk-link uitk-link-layout-default uitk-type-300 is-disabled">
                    <meta itemProp="reviewCount" content={reviews.count} />
                    {reviews.localizedCountWithLabel}
                  </span>
                </div>
              )}
            </>
          )}
        <QueryRoute query={{ pwaDialog: 'reviews' }}>
          <Layer id="infosite-reviews" onDismiss={this.onReviewsDialogClosed}>
            <AlterLocalizationProvider>
              <AbacusExperiment name="PWA_Social_Filter_Review_Score">
                <AbacusControl>
                  <AbacusExperiment name="PWA_Reviews_traveler_type_filter">
                    <AbacusControl>
                      <ReviewsDialog onDismiss={this.onReviewsDialogClosed} />
                    </AbacusControl>
                    <AbacusVariant bucket={1}>
                      <Viewport>
                        <ViewSmall>
                          <ReviewsDialog onDismiss={this.onReviewsDialogClosed} />
                        </ViewSmall>
                        <ViewMedium>
                          <ReviewsDialog onDismiss={this.onReviewsDialogClosed} fullScreen={true} />
                        </ViewMedium>
                        <ViewLarge>
                          <ReviewsDialog onDismiss={this.onReviewsDialogClosed} fullScreen={true} splitView={true} />
                        </ViewLarge>
                      </Viewport>
                    </AbacusVariant>
                  </AbacusExperiment>
                </AbacusControl>
                <AbacusVariant bucket={1}>
                  <Viewport>
                    <ViewSmall>
                      <ReviewsDialog onDismiss={this.onReviewsDialogClosed} />
                    </ViewSmall>
                    <ViewMedium>
                      <ReviewsDialog onDismiss={this.onReviewsDialogClosed} fullScreen={true} />
                    </ViewMedium>
                    <ViewLarge>
                      <ReviewsDialog onDismiss={this.onReviewsDialogClosed} fullScreen={true} splitView={true} />
                    </ViewLarge>
                  </Viewport>
                </AbacusVariant>
              </AbacusExperiment>
            </AlterLocalizationProvider>
          </Layer>
        </QueryRoute>
      </div>
    );
  }

  private onReviewsDialogClosed() {
    // Close the reviews dialog
    this.props.routeStore.goBack();

    if (this.reviewsAction && typeof this.reviewsAction.focus === 'function') {
      this.reviewsAction.focus();

      return;
    }

    this.reviewsWrapper.focus();
  }
}
